import { useState } from "react";

import type { CommandResult, Envelope } from "../api/types";
import { formatTime } from "../lib/format";
import { useFeed } from "../store/useFeed";
import { Empty, Panel } from "./Panel";

/**
 * Диагностическая лента прочих событий шины.
 *
 * Сделки уходят в TapePanel, стаканы — в лесенку (см. store/dispatch.ts), сюда
 * попадает остальное: результаты команд, обновления ордеров, неизвестные типы
 * и — главное — всё, что лента и стакан отвергли как неполное. Именно последнее
 * и делает панель нужной: битое событие видно, а не потеряно молча.
 *
 * Спинной мозг раздачи по-прежнему проверяется отсюда без торгового кода:
 *
 *   docker compose exec redis redis-cli XADD evt.tape '*' payload \
 *     '{"v":1,"type":"trade","ts":"2026-01-01T00:00:00Z","payload":{"trade":{"id":"test"}}}'
 *
 * Такое событие неполно (нет condition_id), поэтому попадёт именно сюда.
 */
export function LiveFeed() {
  const feed = useFeed((s) => s.feed);
  const clear = useFeed((s) => s.clear);
  const [open, setOpen] = useState(false);

  return (
    <Panel
      title="Диагностика"
      subtitle={feed.length > 0 ? `${feed.length} событий` : "прочие события шины"}
      actions={
        <div className="flex items-center gap-3">
          {open && feed.length > 0 && (
            <button onClick={clear} className="text-xs text-[var(--color-muted)] hover:text-white">
              очистить
            </button>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-[var(--color-muted)] hover:text-white"
          >
            {open ? "свернуть" : "развернуть"}
          </button>
        </div>
      }
    >
      {!open ? (
        <p className="text-xs text-[var(--color-muted)]">
          Результаты команд, обновления ордеров и события, которые не удалось разобрать.
        </p>
      ) : feed.length === 0 ? (
        <Empty>Пока пусто — и это хороший знак: разбирать было нечего.</Empty>
      ) : (
        <ul className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto">
          {feed.map((item) => (
            <li
              key={item.id}
              className="rounded border border-[var(--color-border)] px-2.5 py-1.5 text-xs"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium" style={{ color: "var(--color-accent)" }}>
                  {item.env.type}
                </span>
                <time className="num text-[10px] text-[var(--color-muted)]">
                  {formatTime(item.env.ts)}
                </time>
              </div>
              <p className="num mt-0.5 break-all text-[var(--color-muted)]">
                {summarize(item.env)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Однострочная выжимка события — полный JSON в ленте нечитаем.
 *
 * Поля payload — внешние данные из шины, а не наш типобезопасный код: событие
 * с неполными данными должно отрисоваться как сырой JSON, а не уронить рендер
 * обращением к undefined.
 */
function summarize(env: Envelope): string {
  // ?? null: JSON.stringify(undefined) возвращает undefined (не строку),
  // и .slice уронил бы рендер на конверте вовсе без payload.
  const fallback = () => JSON.stringify(env.payload ?? null).slice(0, 160);
  switch (env.type) {
    case "command_result": {
      const r = env.payload as Partial<CommandResult> | null;
      if (!r) return fallback();
      return r.accepted
        ? `принят ${r.order_id ?? r.client_order_id} за ${r.latency_ms} мс`
        : `отклонён: ${r.error ?? "без причины"}`;
    }
    default:
      return fallback();
  }
}
